import { format, isSameDay } from "date-fns";

import { ConversationMessageType } from "@/enum/conversation.enum";

import { ListPerson } from "../../../List/schema/list";
import { buildListDetailLink } from "../../../List/utils/buildListDetailLink";
import { CopywritingCampaign } from "../../../List/pages/ListEditCopywriting/ListEditCopywriting";
import {
	LastOutboundAuthor,
	SenderIdentity,
} from "../../../List/components/CampaignOverview/components/Lead/utils/getOutboxSender";

export type CardChannel = "linkedin" | "email";

/** The LinkedIn invite-acceptance gate — never sends, so it never defines the card channel. */
const INVITE_ACCEPTANCE_GATE_STEP_ID = "n99-cond";

type Conversation = NonNullable<NonNullable<ListPerson["prospect"]>["conversations"]>[number];

/** Maps the scoring result onto the card's score badge (P1/P2/P3/DQ/?). */
export const getLeadScore = (row: ListPerson): string => {
	const overallScore = row.prospect?.scorePersonaCategoryResult?.overallScore;

	if (overallScore === "1") {
		return "P1";
	}

	if (overallScore === "2") {
		return "P2";
	}

	if (overallScore === "3") {
		return "P3";
	}

	if (overallScore === "Disqualified") {
		return "DQ";
	}

	return "?";
};

const isLinkedInType = (type?: string | null): boolean =>
	!!type && (type.toLowerCase().includes("linkedin") || type.startsWith("message"));

/**
 * The card's outreach channel: the next scheduled send step's channel, else the last
 * completed step's, else the last conversation's. Null when nothing indicates one.
 */
export const getCardChannel = (row: ListPerson): CardChannel | null => {
	const schedules = row.schedules ?? [];

	const nextSend = schedules.find(
		(s) => s.processingState === "scheduled" && s.stepId !== INVITE_ACCEPTANCE_GATE_STEP_ID
	);

	if (nextSend?.stepType) {
		return isLinkedInType(nextSend.stepType) ? "linkedin" : "email";
	}

	const lastDone = schedules
		.slice()
		.reverse()
		.find((s) => s.processingState === "done" && s.stepId !== INVITE_ACCEPTANCE_GATE_STEP_ID);

	if (lastDone?.stepType) {
		return isLinkedInType(lastDone.stepType) ? "linkedin" : "email";
	}

	const conversations = getOwnConversations(row);
	const lastConversation = conversations[conversations.length - 1];

	if (lastConversation?.type) {
		return isLinkedInType(lastConversation.type) ? "linkedin" : "email";
	}

	return null;
};

/** The list + campaign a card came from; campaignName is null for pure chat outreach. */
export const getCardProvenance = (
	row: ListPerson
): {
	listName: string;
	listTo: string;
	campaignName: string | null;
} => {
	const listCampaign = row.campaignId
		? (row.list.campaignConfig?.[row.campaignId] as CopywritingCampaign | undefined)
		: undefined;

	const ownCampaign = row.campaignConfig as CopywritingCampaign | null | undefined;

	return {
		listName: row.list.name ?? "",
		// The provenance row's list name is a link back to the list (PREC-4028) — the tab is
		// resolved from the lead's own status so the destination actually shows them.
		listTo: buildListDetailLink({
			listId: row.list.id,
			prospectName: row.prospect?.name,
			assignStatus: row.assignStatus,
		}),
		campaignName: listCampaign?.name ?? ownCampaign?.name ?? null,
	};
};

/** Same-day timestamps show the time, older ones the date. */
export const formatCardTime = (iso: string | null | undefined, now: Date = new Date()): string => {
	if (!iso) {
		return "";
	}

	const date = new Date(iso);

	if (Number.isNaN(date.getTime())) {
		return "";
	}

	return isSameDay(date, now) ? format(date, "HH:mm") : format(date, "MMM d");
};

/** Conversations belonging to this list person (a prospect can sit on several lists). */
const getOwnConversations = (row: ListPerson): Conversation[] =>
	(row.prospect?.conversations ?? []).filter(
		(conversation) => conversation?.listPersonId === row.id
	);

/** The last message we sent to this lead — the one the Outbox card previews. */
const findLastOutbound = (row: ListPerson): Conversation | undefined =>
	getOwnConversations(row)
		.slice()
		.reverse()
		.find(
			(conversation) =>
				!!conversation?.type?.includes("sent") && !conversation?.scheduled && conversation?.message
		);

/** The last message we sent to this lead (for the card preview). */
export const getLastOutboundMessage = (
	row: ListPerson
): { message: string; sentAt: string | null } | null => {
	const sent = findLastOutbound(row);

	if (!sent?.message) {
		return null;
	}

	return { message: sent.message, sentAt: sent.sentAt ?? null };
};

/**
 * Who wrote the message the card previews (PREC-4085). Read off the same conversation as
 * `getLastOutboundMessage`, so avatar and preview can never disagree. Null when it names no user.
 */
export const getLastOutboundAuthor = (row: ListPerson): LastOutboundAuthor | null => {
	const sent = findLastOutbound(row);
	const user = sent?.user;

	if (!sent || !user) {
		return null;
	}

	return {
		userId: user.id,
		name: `${user.firstName} ${user.lastName}`,
		firstName: user.firstName,
		lastName: user.lastName,
		type: isLinkedInType(sent.type) ? "linkedin" : "email",
		emailAddress: sent.senderEmail ?? undefined,
		sentAt: sent.sentAt ?? null,
	};
};

/**
 * Sender identity for rows without a usable schedule record (pure chat outreach): the user on
 * the last outbound conversation. Null when no outbound conversation names a user.
 */
export const getSenderFromConversations = (row: ListPerson): SenderIdentity | null => {
	const sent = getOwnConversations(row)
		.slice()
		.reverse()
		.find((conversation) => !!conversation?.type?.includes("sent") && !conversation?.scheduled);

	const user = sent?.user;

	if (!user) {
		return null;
	}

	return {
		name: `${user.firstName} ${user.lastName}`,
		firstName: user.firstName,
		lastName: user.lastName,
		type: isLinkedInType(sent.type) ? "linkedin" : "email",
		emailAddress: sent.senderEmail ?? undefined,
	};
};

/** The last inbound reply (for the Inbox card preview + sentiment chip). */
export const getLastInboundResponse = (row: ListPerson): Conversation | undefined => {
	const responses = row.prospect?.conversations?.filter(
		(conversation) =>
			conversation.messageType === ConversationMessageType.MESSAGE &&
			(conversation.type === "message_received" || conversation.type === "mail_received")
	);

	return responses?.[responses.length - 1];
};

/** Collapses an HTML message to plain preview text. */
export const getPreviewText = (html?: string | null): string => {
	if (!html) {
		return "";
	}

	if (typeof DOMParser === "undefined") {
		return html.replace(/<[^>]*>/g, " ").trim();
	}

	const doc = new DOMParser().parseFromString(html, "text/html");

	return doc.body.textContent?.replace(/\s+/g, " ").trim() || "";
};

/**
 * The campaign behind a card: the lead's own override, else the list campaign the lead is
 * assigned to, else the list's language-matching or default campaign. Shared by both lead
 * cards (extracted from the identical memos in the legacy InboxLead/OutboxLead).
 */
export const resolveCardCampaignConfig = (row: ListPerson): CopywritingCampaign | undefined => {
	if (row.campaignConfig) {
		return row.campaignConfig as CopywritingCampaign;
	}

	const allCampaigns = row.list.campaignConfig as
		| { [key: string]: CopywritingCampaign }
		| undefined;

	if (!allCampaigns) {
		return undefined;
	}

	const allValidCampaigns = Object.keys(allCampaigns).filter(
		(campaign) => allCampaigns[campaign].isValid
	);

	if (allValidCampaigns.length === 0) {
		return undefined;
	}

	if (row.campaignId && allCampaigns[row.campaignId]) {
		return allCampaigns[row.campaignId];
	}

	const withId = (id: string | undefined): CopywritingCampaign | undefined =>
		id ? ({ ...(allCampaigns[id] || {}), id } as CopywritingCampaign) : undefined;

	const languages = row.prospect?.language
		?.split(",")
		?.map((lang: string) => lang.trim().toLowerCase());

	const languageBasedDefaultCampaign = withId(
		allValidCampaigns.find((campaign) =>
			allCampaigns[campaign]?.languages?.some((lang: string) =>
				languages?.includes(lang.toLowerCase())
			)
		)
	);

	const defaultCampaign = withId(
		allValidCampaigns.find((campaign) => allCampaigns[campaign].isDefault)
	);

	const nonDefaultFallback = withId(
		allValidCampaigns.find((campaign) => allCampaigns[campaign].isDefault === false)
	);

	return languageBasedDefaultCampaign || defaultCampaign || nonDefaultFallback;
};
